import React, { useCallback, useMemo, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./ArticleRichTextEditor.css";
import container from "@/lib/di/container";
import { MediaRepo } from "@/core/repos/tables/media.repo";
import { message } from "antd";
import { RcFile } from "antd/es/upload";

const mediaRepo = container.get(MediaRepo);

const EDITOR_FORMATS = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "link",
    "image",
];

interface ArticleRichTextEditorProps {
    value?: string;
    onChange?: (value: string) => void;
    onEnsureArticleId: () => Promise<string | null>;
    disabled?: boolean;
    placeholder?: string;
}

export const ArticleRichTextEditor: React.FC<ArticleRichTextEditorProps> = ({
    value,
    onChange,
    onEnsureArticleId,
    disabled = false,
    placeholder = "Soạn nội dung bản tin...",
}) => {
    const quillRef = useRef<ReactQuill>(null);
    const [messageApi, contextHolder] = message.useMessage();
    const onEnsureArticleIdRef = useRef(onEnsureArticleId);
    onEnsureArticleIdRef.current = onEnsureArticleId;

    const insertImage = useCallback((url: string) => {
        const editor = quillRef.current?.getEditor();
        if (!editor) return;
        const range = editor.getSelection(true);
        const index = range?.index ?? editor.getLength();
        editor.insertEmbed(index, "image", url);
        editor.setSelection(index + 1);
    }, []);

    const uploadImageFromPicker = useCallback(async () => {
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", "image/*");
        input.click();

        input.onchange = async () => {
            const file = input.files?.[0];
            if (!file) return;

            if (!file.type.startsWith("image/")) {
                messageApi.error("Vui lòng chọn file ảnh");
                return;
            }

            const articleId = await onEnsureArticleIdRef.current();
            if (!articleId) return;

            const hide = messageApi.loading("Đang tải ảnh lên...", 0);
            try {
                const uploaded = await mediaRepo.uploadCompressed(
                    {
                        id: "",
                        name: file.name,
                        objectType: "Article",
                        referenceId: articleId,
                    },
                    file as RcFile,
                    { quality: 80, maxWidth: 1920 }
                );

                if (!uploaded?.id) {
                    messageApi.error("Upload ảnh thất bại");
                    return;
                }

                const publicUrl = await mediaRepo.resolvePublicUrl(
                    "Article",
                    articleId,
                    uploaded.id
                );

                if (!publicUrl) {
                    messageApi.error("Không lấy được URL ảnh sau khi upload");
                    return;
                }

                insertImage(publicUrl);
                messageApi.success("Đã chèn ảnh vào nội dung");
            } catch {
                messageApi.error("Có lỗi khi upload ảnh");
            } finally {
                hide();
            }
        };
    }, [insertImage, messageApi]);

    const modules = useMemo(
        () => ({
            toolbar: {
                container: [
                    [{ header: [1, 2, 3, false] }],
                    ["bold", "italic", "underline", "strike"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["link", "image"],
                    ["clean"],
                ],
                handlers: {
                    image: () => {
                        void uploadImageFromPicker();
                    },
                },
            },
        }),
        [uploadImageFromPicker]
    );

    return (
        <>
            {contextHolder}
            <div
                className={`article-rich-text-editor${disabled ? " is-disabled" : ""}`}
            >
                <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={value ?? ""}
                    onChange={(content) => onChange?.(content)}
                    modules={modules}
                    formats={EDITOR_FORMATS}
                    placeholder={placeholder}
                    readOnly={disabled}
                />
            </div>
        </>
    );
};
